import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Send, Bug, HelpCircle, Lightbulb, MoreHorizontal, AlertTriangle } from 'lucide-react';
import './Tickets.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const CATEGORIES = [
  { value: 'BUG', label: 'Bug', icon: Bug, description: 'Signaler un problème technique' },
  { value: 'QUESTION', label: 'Question', icon: HelpCircle, description: 'Poser une question sur l\'utilisation' },
  { value: 'SUGGESTION', label: 'Suggestion', icon: Lightbulb, description: 'Proposer une amélioration' },
  { value: 'OTHER', label: 'Autre', icon: MoreHorizontal, description: 'Autre demande' }
];

function TicketCreateModal({
  token,
  user,
  userSubscription,
  currentView,
  previousView,
  selectedClientId,
  selectedClientName,
  selectedSiteId,
  selectedSiteName,
  selectedPlatform,
  globalConnectors,
  userOrganizations,
  onClose,
  onTicketCreated
}) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('QUESTION');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const captureContext = () => ({
    // Navigation - utiliser previousView pour la dernière page consultée avant Support
    lastVisitedPage: previousView || 'home',
    currentPage: currentView,
    currentUrl: window.location.href,

    // Sélections
    selectedClientId,
    selectedClientName,
    selectedSiteId,
    selectedSiteName,
    selectedPlatform,

    // Utilisateur
    userPlan: userSubscription?.plan || 'FREE',
    userRole: user?.role,
    userRegisteredAt: user?.createdAt,
    userOrganization: userOrganizations?.[0]?.name,

    // Connecteurs
    connectedPlatforms: globalConnectors ? [...new Set(globalConnectors.map(c => c.provider).filter(Boolean))] : [],
    totalAccounts: globalConnectors?.length || 0,

    // Technique
    userAgent: navigator.userAgent,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    timestamp: new Date().toISOString()
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subject.trim()) {
      setError('Le sujet est requis');
      return;
    }

    if (!message.trim()) {
      setError('Le message est requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const context = captureContext();

      const response = await axios.post(
        `${API_URL}/api/tickets`,
        {
          subject: subject.trim(),
          message: message.trim(),
          category,
          context
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        onTicketCreated(response.data.ticket);
      }
    } catch (err) {
      console.error('Erreur création ticket:', err);
      setError(err.response?.data?.error || 'Erreur lors de la création du ticket');
    } finally {
      setLoading(false);
    }
  };

  const modal = (
    <div className="ticket-modal-overlay" onClick={onClose}>
      <div className="ticket-modal" onClick={e => e.stopPropagation()}>
        <div className="ticket-modal-header">
          <h2>Nouveau ticket</h2>
          <button className="ticket-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ticket-modal-form">
          {error && (
            <div className="ticket-modal-error">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          <div className="ticket-form-group">
            <label htmlFor="subject">Sujet *</label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Décrivez brièvement votre demande"
              maxLength={200}
              required
            />
          </div>

          <div className="ticket-form-group">
            <label>Catégorie</label>
            <div className="ticket-category-options">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    className={`ticket-category-btn ${category === cat.value ? 'active' : ''}`}
                    onClick={() => setCategory(cat.value)}
                  >
                    <Icon size={18} />
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ticket-form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Décrivez votre problème ou question en détail..."
              rows={6}
              required
            />
          </div>

          <div className="ticket-modal-footer">
            <button
              type="button"
              className="ticket-modal-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="ticket-modal-submit"
              disabled={loading || !subject.trim() || !message.trim()}
            >
              {loading ? (
                <>
                  <span className="loading-spinner" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Envoyer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

export default TicketCreateModal;
